import React, { Suspense, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { Router, Route } from 'react-router'
import { I18nextProvider } from 'react-i18next'
import { PersistGate } from 'redux-persist/es/integration/react'
import { QueryParamProvider, ExtendedStringifyOptions, transformSearchStringJsonSafe } from 'use-query-params'

// instead of antd.css to avoid module warnings from antd. https://github.com/ant-design/ant-design/issues/33327
import 'antd/dist/antd.min.css'
import { ConfigProvider, Spin } from 'antd'

import { Locale } from 'antd/lib/locale-provider'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import en_GB from 'antd/lib/locale-provider/en_GB'
import de_DE from 'antd/lib/locale-provider/de_DE'
import sk_SK from 'antd/lib/locale-provider/sk_SK'
import Routes from './routes/Routes'

import rootReducer from './reducers'

// utils
import configureStore from './utils/configureStore'
import i18n from './utils/i18n'
import { history } from './utils/history'
import { APP_LANGUAGE } from './utils/enums'

dayjs.extend(weekday)
dayjs.extend(localeData)

const queryStringifyOptions: ExtendedStringifyOptions = {
	transformSearchString: transformSearchStringJsonSafe
}

const { store, persistor } = configureStore(rootReducer)

const getAntdLocale = (language: string): Locale => {
	if (language === APP_LANGUAGE.EN) {
		return en_GB
	}
	if (language === APP_LANGUAGE.DE) {
		return de_DE
	}
	return sk_SK
}

const App = () => {
	const [antdLocale, setAntdLocale] = useState<Locale>(() => getAntdLocale(i18n.language))

	useEffect(() => {
		const syncLocale = (language: string) => {
			document.documentElement.setAttribute('lang', language)
			setAntdLocale(getAntdLocale(language))
			dayjs.locale(language)
		}

		syncLocale(i18n.language)
		i18n.on('languageChanged', syncLocale)

		return () => {
			i18n.off('languageChanged', syncLocale)
		}
	}, [])

	return (
		<Suspense
			fallback={
				<div className={'suspense-loading-spinner'}>
					<Spin />
				</div>
			}
		>
			<I18nextProvider i18n={i18n}>
				<PersistGate
					loading={
						<div className={'suspense-loading-spinner'}>
							<Spin />
						</div>
					}
					persistor={persistor}
				>
					<ConfigProvider locale={antdLocale}>
						<Provider store={store}>
							<Router history={history}>
								<QueryParamProvider ReactRouterRoute={Route} stringifyOptions={queryStringifyOptions}>
									<Routes />
								</QueryParamProvider>
							</Router>
						</Provider>
					</ConfigProvider>
				</PersistGate>
			</I18nextProvider>
		</Suspense>
	)
}

export default App
